/**
 * useCatcherMutations - Accept and Redo mutations
 *
 * Follows production-orders pattern with:
 * - Proper cache invalidation using tRPC utils
 * - Optimistic updates for instant feedback
 * - Clear error handling with toast notifications
 */
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';

// Redo reason — dynamic per-org, no longer a fixed union
export type RedoReason = string;

export interface AcceptGarmentResult {
  success: boolean;
  garmentUnitId: string;
  newStatus: string;
  orderComplete: boolean;
  binLabel: string | null;
  itemsCaught: number;
  totalItems: number;
  orderName: string;
  /** True when this garment belongs to a pre-print batch (stock swap occurred) */
  isPrePrintBatch?: boolean;
}

/** BLU-197: result of claiming a work bench for an order at the catcher station. */
export interface AssignBenchResult {
  success: boolean;
  benchId: string;
  benchLabel: string;
}

interface CatcherMutationCallbacks {
  onAcceptSuccess?: (data: AcceptGarmentResult) => void;
  onRedoSuccess?: () => void;
  onUndoSuccess?: () => void;
}

export function useCatcherMutations(callbacks?: CatcherMutationCallbacks) {
  const { t } = useTranslation('production');
  const utils = trpc.useUtils();

  const acceptGarment = trpc.catcher.acceptGarment.useMutation({
    onSuccess: (data) => {
      // Invalidate catcher queries to refresh stats + recently caught list
      utils.catcher.getCatcherStats.invalidate();
      utils.catcher.getRecentlyCaught.invalidate();
      // Invalidate batch queries so progress bars update in BatchDetailScreen
      utils.productionOrders.getBatch.invalidate();
      utils.productionOrders.getBatchLabels.invalidate();

      callbacks?.onAcceptSuccess?.(data as AcceptGarmentResult);
    },
    onError: () => {
      // Error handled by CatcherPage full-screen error modal
    },
  });

  const undoCatch = trpc.catcher.undoCatch.useMutation({
    onSuccess: () => {
      // Invalidate same queries as accept (stats + batch progress + recently caught)
      utils.catcher.getCatcherStats.invalidate();
      utils.catcher.getRecentlyCaught.invalidate();
      utils.productionOrders.getBatch.invalidate();
      utils.productionOrders.getBatchLabels.invalidate();

      // Invalidate garment lookup so rescan fetches fresh progress
      utils.catcher.getGarmentByQR.invalidate();

      callbacks?.onUndoSuccess?.();
    },
    onError: () => {
      // Error handled by CatcherPage full-screen error modal
    },
  });

  const createRedo = trpc.catcher.createRedo.useMutation({
    onSuccess: () => {
      // Invalidate catcher queries to refresh stats
      utils.catcher.getCatcherStats.invalidate();

      // Invalidate production issues so Issues > Redos tab shows the new redo
      utils.productionOrders.issues.list.invalidate();
      utils.productionOrders.issues.getCounts.invalidate();

      // Invalidate QA reports so defect stats reflect the new redo
      utils.qaReports.getQAReportStats.invalidate();

      toast({
        title: t('catcher.redoCreated'),
        description: t('catcher.redoCreatedDesc'),
      });

      callbacks?.onRedoSuccess?.();
    },
    onError: () => {
      // Error handled by CatcherPage full-screen error modal
    },
  });

  // BLU-197: claim a scanned work bench for the active garment's order.
  // Errors (NOT_FOUND / PRECONDITION_FAILED) are surfaced by the caller as a
  // station-blocking modal with audio — handled in CatcherPage, not here.
  const assignBench = trpc.catcher.assignBench.useMutation({
    onSuccess: () => {
      // A bench claim mutates order container state — refresh the same surfaces
      // an accept would, plus the garment lookup so a re-scan reflects the bench.
      utils.catcher.getGarmentByQR.invalidate();
      utils.productionOrders.getBatch.invalidate();
      utils.productionOrders.getBatchLabels.invalidate();
    },
    onError: () => {
      // Error handled by CatcherPage full-screen error modal
    },
  });

  return {
    acceptGarment,
    undoCatch,
    createRedo,
    assignBench,
    isAccepting: acceptGarment.isPending,
    isUndoing: undoCatch.isPending,
    isCreatingRedo: createRedo.isPending,
    isAssigningBench: assignBench.isPending,
  };
}
